import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CalendarPlus, FilePenLine, FileStack, FileText, History, LogIn, LogOut, QrCode, Smartphone, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Permissions as P, type AttendanceSummaryDto, type AttendanceTodayDto, type ClockResultDto, type MyEventDto, type TodayDeviceDto, type TodaySessionDto } from '@fas/types';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn, Dialog, DialogContent, Skeleton, StatusBadge } from '@fas/ui';
import { api, errorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cacheToday, recordServerTime, useOfflineQueue } from '@/lib/offline-queue';
import { useCan, useMe } from '@/lib/permissions';
import { MODALITY_LABELS } from '@/features/scheduling/labels';
import { MyEventsList } from '@/features/events/MyEventsList';
import { ClockDialog, sessionTitle } from './ClockDialog';
import { useAttendanceLiveRefresh, useNow } from './hooks';
import { ACTUAL_MODALITY, ATTENDANCE_STATUS, clockRange, clockTime, hours, LOCATION_STATUS, TIME_STATUS, WORK_ARRANGEMENT } from './labels';

type ClockTarget = { session: TodaySessionDto; kind: 'in' | 'out'; offline?: boolean } | null;

/** Clock events of a session saved on this device and not sent yet (Phase 2D). */
type Saved = { clockIn: boolean; clockOut: boolean };

function SavedBadges({ saved }: { saved: Saved }) {
  if (!saved.clockIn && !saved.clockOut) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {saved.clockIn ? <Badge tone="warning">Clock-in saved on this device, not sent yet</Badge> : null}
      {saved.clockOut ? <Badge tone="warning">Clock-out saved on this device, not sent yet</Badge> : null}
    </div>
  );
}

/** Whether the clock-in window is open, using the server clock (corrected for device skew). */
function windowOpen(s: TodaySessionDto, serverNow: number): boolean {
  if (!s.actions.clockInOpensAt || !s.actions.clockInClosesAt) return s.actions.canClockIn;
  return serverNow >= Date.parse(s.actions.clockInOpensAt) && serverNow < Date.parse(s.actions.clockInClosesAt);
}

/** Where the policy requires a room code, the clock-in starts from the display (Phase 2E). */
function ScanHint({ daily }: { daily?: boolean }) {
  return (
    <p className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
      <QrCode className="mt-0.5 size-4 shrink-0" aria-hidden />
      {daily
        ? 'Scan the code on a campus display with your phone’s camera to time in.'
        : 'Scan the code on the room’s display (or the building’s kiosk) with your phone’s camera to clock in.'}
    </p>
  );
}

function StatusLine({ s, timeZone }: { s: TodaySessionDto; timeZone: string }) {
  const status = ATTENDANCE_STATUS[s.attendanceStatus];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <StatusBadge label={status.label} tone={status.tone} />
      {s.timeStatus && s.actualClockIn ? <StatusBadge label={TIME_STATUS[s.timeStatus].label} tone={TIME_STATUS[s.timeStatus].tone} /> : null}
      {s.locationStatus && s.actualClockIn ? <StatusBadge label={LOCATION_STATUS[s.locationStatus].label} tone={LOCATION_STATUS[s.locationStatus].tone} /> : null}
      {s.actualClockIn ? (
        <span className="text-xs text-slate-600">
          In {clockTime(s.actualClockIn, timeZone)}
          {s.actualClockOut ? ` · Out ${clockTime(s.actualClockOut, timeZone)}` : ''}
          {s.lateMinutes > 0 ? ` · ${s.lateMinutes} min late` : ''}
        </span>
      ) : null}
    </div>
  );
}

function ClassCard({
  s,
  timeZone,
  serverNow,
  marker,
  saved,
  onClock,
}: {
  s: TodaySessionDto;
  timeZone: string;
  serverNow: number;
  marker: 'NOW' | 'NEXT' | null;
  saved: Saved;
  onClock: (kind: 'in' | 'out', offline?: boolean) => void;
}) {
  // A clock-in waiting on the device: the clock-out waits behind it, in order.
  const canIn = !saved.clockIn && s.attendanceStatus === 'SCHEDULED' && windowOpen(s, serverNow);
  const canOut = !saved.clockOut && (s.actions.canClockOut || saved.clockIn);
  const where = [s.locationName, s.actualModality ? ACTUAL_MODALITY[s.actualModality] : s.scheduledModality ? MODALITY_LABELS[s.scheduledModality] : null].filter(Boolean).join(' | ');
  return (
    <article
      aria-label={`${sessionTitle(s)} ${clockRange(s.scheduledStart, s.scheduledEnd, timeZone)}`}
      className={cn('rounded-lg border bg-white p-4 shadow-sm', marker === 'NOW' ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold">{s.courseTitle}</p>
          <p className="text-sm text-slate-700">
            {sessionTitle(s)} · {clockRange(s.scheduledStart, s.scheduledEnd, timeZone)}
          </p>
          <p className="text-sm text-slate-600">{where}</p>
        </div>
        {marker ? <Badge tone={marker === 'NOW' ? 'info' : 'neutral'}>{marker === 'NOW' ? 'Now' : 'Next'}</Badge> : null}
      </div>
      <div className="mt-3">
        <StatusLine s={s} timeZone={timeZone} />
      </div>
      <SavedBadges saved={saved} />
      {s.substitutedFacultyName ? <p className="mt-2 text-sm text-slate-700">Substitute session — covering for {s.substitutedFacultyName}.</p> : null}
      {s.remarks && (s.attendanceStatus === 'CANCELLED' || s.attendanceStatus === 'CLASS_SUSPENDED') ? <p className="mt-2 text-sm text-slate-700">{s.remarks}</p> : null}
      {s.meetingUrl && s.attendanceStatus !== 'CANCELLED' ? (
        <a href={s.meetingUrl} target="_blank" rel="noreferrer noopener" className="mt-2 inline-block text-sm font-medium text-blue-700 hover:underline">
          Open meeting link
        </a>
      ) : null}
      <div className="mt-3">
        {canOut ? (
          <Button size="lg" className="w-full sm:w-auto" onClick={() => onClock('out', saved.clockIn)}>
            <LogOut aria-hidden /> Clock out
          </Button>
        ) : canIn && s.actions.qrScanRequired ? (
          <ScanHint />
        ) : canIn ? (
          <Button size="lg" className="w-full sm:w-auto" onClick={() => onClock('in')}>
            <LogIn aria-hidden /> Clock in
          </Button>
        ) : s.actions.hint ? (
          <p className="text-sm text-slate-600">{s.actions.hint}</p>
        ) : null}
      </div>
    </article>
  );
}

function DailyCard({
  segments,
  timeZone,
  serverNow,
  savedFor,
  onClock,
}: {
  segments: TodaySessionDto[];
  timeZone: string;
  serverNow: number;
  savedFor: (sessionId: string) => Saved;
  onClock: (s: TodaySessionDto, kind: 'in' | 'out', offline?: boolean) => void;
}) {
  const open = segments.find((s) => !savedFor(s.id).clockOut && (s.actions.canClockOut || savedFor(s.id).clockIn));
  const next = segments.find((s) => !savedFor(s.id).clockIn && s.attendanceStatus === 'SCHEDULED' && windowOpen(s, serverNow));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily attendance</CardTitle>
        <CardDescription>Your workday time-in and time-out, recorded separately from class sessions.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-2">
          {segments.map((s) => (
            <li key={s.id} className="flex flex-col gap-1 rounded-md border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium">
                  {s.dailySegment === 'FULL_DAY' ? 'Workday' : s.dailySegment === 'AM' ? 'Morning' : 'Afternoon'} · {clockRange(s.scheduledStart, s.scheduledEnd, timeZone)}
                  {s.workArrangement && s.workArrangement !== 'ONSITE' ? ` · ${WORK_ARRANGEMENT[s.workArrangement]}` : ''}
                </p>
                <StatusLine s={s} timeZone={timeZone} />
                <SavedBadges saved={savedFor(s.id)} />
              </div>
            </li>
          ))}
        </ul>
        {open ? (
          <Button size="lg" className="w-full sm:w-auto" onClick={() => onClock(open, 'out', savedFor(open.id).clockIn)}>
            <LogOut aria-hidden /> Time out
          </Button>
        ) : next && next.actions.qrScanRequired ? (
          <ScanHint daily />
        ) : next ? (
          <Button size="lg" className="w-full sm:w-auto" onClick={() => onClock(next, 'in')}>
            <LogIn aria-hidden /> Time in
          </Button>
        ) : (
          <p className="text-sm text-slate-600">{segments.find((s) => s.actions.hint)?.actions.hint ?? 'No time-in is due right now.'}</p>
        )}
      </CardContent>
    </Card>
  );
}

const DEVICE_NOTICE: Record<'REQUIRE' | 'FLAG', Record<Exclude<TodayDeviceDto['status'], 'REGISTERED'>, string>> = {
  REQUIRE: {
    UNREGISTERED: 'Your attendance policy requires a registered device. Register this browser under My account → Devices before you clock in.',
    PENDING_APPROVAL: 'This device is waiting for approval by Human Resources. You can clock in from it once it is approved.',
    REVOKED: 'The registration of this device was revoked. Clock in from your registered device, or register this one again.',
  },
  FLAG: {
    UNREGISTERED: 'Clock-ins from devices that are not registered are reviewed. Register this browser under My account → Devices.',
    PENDING_APPROVAL: 'This device is waiting for approval by Human Resources; until then its clock-ins are reviewed.',
    REVOKED: 'The registration of this device was revoked; its clock-ins are reviewed. Register it again or use your registered device.',
  },
};

/** Tells the faculty member when the attendance policy expects a registered device (Phase 2B). */
function DeviceNotice({ device }: { device: TodayDeviceDto }) {
  const canRegister = useCan(P.DEVICE_REGISTER_SELF);
  if (device.registration === 'OFF' || device.status === 'REGISTERED') return null;
  const required = device.registration === 'REQUIRE';
  return (
    <Alert tone={required ? 'warning' : 'info'} title={device.status === 'PENDING_APPROVAL' ? 'Device awaiting approval' : required ? 'Register this device to clock in' : 'Register this device'}>
      <p>{DEVICE_NOTICE[device.registration][device.status]}</p>
      {canRegister && device.status !== 'PENDING_APPROVAL' ? (
        <Button variant="outline" size="sm" className="mt-2" asChild>
          <Link to="/account#devices">
            <Smartphone aria-hidden /> Manage devices
          </Link>
        </Button>
      ) : null}
    </Alert>
  );
}

/** Institutional events of the day the faculty member is invited to (Phase 2E). */
function TodayEvents({ timeZone }: { timeZone: string }) {
  const canCheckIn = useCan(P.EVENT_CHECK_IN);
  const events = useQuery({
    queryKey: ['/events', 'mine', 'today'],
    queryFn: ({ signal }) => api.get<MyEventDto[]>('/events/mine', undefined, signal),
    enabled: canCheckIn,
    refetchInterval: 60_000,
  });
  const items = (events.data ?? []).filter((e) => e.event.status === 'PUBLISHED');
  if (items.length === 0) return null;
  return (
    <section aria-labelledby="today-events" className="space-y-3">
      <h2 id="today-events" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Today&apos;s events
      </h2>
      <MyEventsList items={items} timeZone={timeZone} />
    </section>
  );
}

function CutoffSummary() {
  const summary = useQuery({
    queryKey: ['/attendance', 'summary', 'current'],
    queryFn: ({ signal }) => api.get<AttendanceSummaryDto>('/attendance/summary', undefined, signal),
  });
  // §31.2 "Pending approvals": the faculty member's own requests still in approval.
  const pendingRequests = useQuery({
    queryKey: ['/approvals', 'requested', 'pending-count'],
    queryFn: ({ signal }) => api.getPage<unknown>('/approvals', { view: 'requested', status: 'PENDING', pageSize: 1 }, signal).then((r) => r.meta.total),
  });
  const d = summary.data;
  const extra = d ? (d.byLoadType.OVERLOAD?.renderedMinutes ?? 0) + (d.byLoadType.EXTRA_LOAD?.renderedMinutes ?? 0) : 0;
  const tiles: Array<[string, string]> = d
    ? [
        ['Scheduled', hours(d.teaching.scheduledMinutes)],
        ['Rendered', hours(d.teaching.renderedMinutes)],
        ['Verified', hours(d.teaching.verifiedMinutes)],
        ['Approved payable', hours(d.teaching.approvedPayableMinutes)],
        ['Late', `${d.teaching.lateMinutes} min`],
        ['Undertime', `${d.teaching.undertimeMinutes} min`],
        ['Pending review', String(d.teaching.pendingVerification)],
        ['Missing clock-out', String(d.teaching.missingClockOut)],
        ['Makeup sessions', `${d.byLoadType.MAKEUP?.sessions ?? 0} (${hours(d.byLoadType.MAKEUP?.renderedMinutes ?? 0)})`],
        ['Overload / extra load', hours(extra)],
        ['Requests in approval', pendingRequests.data === undefined ? '—' : String(pendingRequests.data)],
      ]
    : [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>This cut-off</CardTitle>
        <CardDescription>{d ? `${formatDate(d.from)} – ${formatDate(d.to)} · teaching and duty sessions` : 'Loading…'}</CardDescription>
      </CardHeader>
      <CardContent>
        {summary.error ? <Alert tone="danger">{errorMessage(summary.error)}</Alert> : null}
        {summary.isLoading ? <Skeleton className="h-16 w-full" /> : null}
        {d ? (
          <>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {tiles.map(([label, value]) => (
                <div key={label} className="rounded-md bg-slate-50 p-3">
                  <dt className="text-xs text-slate-600">{label}</dt>
                  <dd className="text-lg font-semibold">{value}</dd>
                </div>
              ))}
            </dl>
            {d.daily ? (
              <p className="mt-3 text-sm text-slate-700">
                Daily attendance: {hours(d.daily.renderedMinutes)} rendered of {hours(d.daily.requiredMinutes)} required · {d.daily.daysPresent} day(s) present
                {d.daily.daysAbsent ? ` · ${d.daily.daysAbsent} absent` : ''}
                {d.daily.lateMinutes ? ` · ${d.daily.lateMinutes} min late` : ''}
              </p>
            ) : null}
            <p className="mt-2 text-xs text-slate-500">Hours are attendance totals for payroll support; no pay is calculated here.</p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Faculty home (§31): today's schedule with clock-in/out, daily attendance and cut-off totals. */
export function FacultyToday() {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<ClockTarget>(null);
  const canCorrect = useCan(P.ATTENDANCE_CORRECT_REQUEST);
  const canMakeup = useCan(P.MAKEUP_REQUEST);
  const canProof = useCan(P.PROOF_UPLOAD);
  const today = useQuery({
    queryKey: ['/attendance', 'today'],
    queryFn: ({ signal }) => api.get<AttendanceTodayDto>('/attendance/today', undefined, signal),
    refetchInterval: 60_000,
  });
  useAttendanceLiveRefresh();
  const now = useNow(15_000);
  const data = today.data;
  // Correct for a wrong device clock: the server clock decides when windows open (§71).
  const skew = data ? Date.parse(data.serverTime) - today.dataUpdatedAt : 0;
  const serverNow = now.getTime() + skew;
  // Kept on the device for recording offline: the server offset and the day's sessions (Phase 2D).
  useEffect(() => {
    if (!data) return;
    recordServerTime(data.serverTime, today.dataUpdatedAt);
    void cacheToday(data);
  }, [data, today.dataUpdatedAt]);
  const me = useMe();
  const queued = useOfflineQueue((s) => s.events);
  const savedFor = (sessionId: string): Saved => {
    const mine = queued.filter((e) => e.sessionId === sessionId && e.userId === me?.user.id && e.status === 'PENDING');
    return { clockIn: mine.some((e) => e.kind === 'CLOCK_IN'), clockOut: mine.some((e) => e.kind === 'CLOCK_OUT') };
  };

  const classes = data?.classes ?? [];
  const current = classes.find((c) => c.attendanceStatus === 'CLOCKED_IN') ?? classes.find((c) => Date.parse(c.scheduledStart) <= serverNow && serverNow < Date.parse(c.scheduledEnd) && c.attendanceStatus === 'SCHEDULED');
  const next = classes.find((c) => c !== current && c.attendanceStatus === 'SCHEDULED' && Date.parse(c.scheduledStart) > serverNow);

  const done = async (result: ClockResultDto) => {
    if (!result.replayed) toast.success(target?.kind === 'out' ? 'Clock-out recorded' : 'Clock-in recorded');
    await queryClient.invalidateQueries({ queryKey: ['/attendance'] });
  };

  return (
    <div className="space-y-4">
      {today.error ? <Alert tone="danger">{errorMessage(today.error)}</Alert> : null}
      {today.isLoading ? <Skeleton className="h-32 w-full" /> : null}
      {data?.device ? <DeviceNotice device={data.device} /> : null}

      {data && data.daily.length > 0 ? (
        <DailyCard segments={data.daily} timeZone={data.timeZone} serverNow={serverNow} savedFor={savedFor} onClock={(session, kind, offline) => setTarget({ session, kind, offline })} />
      ) : null}

      {data ? (
        <section aria-labelledby="today-classes" className="space-y-3">
          <h2 id="today-classes" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Today&apos;s classes
          </h2>
          {classes.length === 0 ? <p className="text-sm text-slate-600">You have no classes scheduled today.</p> : null}
          <div className="grid gap-3 lg:grid-cols-2">
            {classes.map((c) => (
              <ClassCard
                key={c.id}
                s={c}
                timeZone={data.timeZone}
                serverNow={serverNow}
                marker={c === current ? 'NOW' : c === next ? 'NEXT' : null}
                saved={savedFor(c.id)}
                onClock={(kind, offline) => setTarget({ session: c, kind, offline })}
              />
            ))}
          </div>
        </section>
      ) : null}

      {data ? <TodayEvents timeZone={data.timeZone} /> : null}

      <CutoffSummary />

      <nav aria-label="Attendance shortcuts" className="flex flex-wrap gap-2">
        <Button variant="outline" asChild>
          <Link to="/my-schedule">
            <CalendarClock aria-hidden /> View schedule
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/attendance/history">
            <History aria-hidden /> Attendance history
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/attendance/dtr">
            <FileText aria-hidden /> DTR / report
          </Link>
        </Button>
        {canCorrect ? (
          <Button variant="outline" asChild>
            <Link to="/attendance/history" title="Open the record to correct and choose “Request correction”">
              <FilePenLine aria-hidden /> Submit correction
            </Link>
          </Button>
        ) : null}
        {canMakeup ? (
          <Button variant="outline" asChild>
            <Link to="/requests/makeup?new=1">
              <CalendarPlus aria-hidden /> Request makeup
            </Link>
          </Button>
        ) : null}
        {canProof ? (
          <Button variant="outline" asChild>
            <Link to="/attendance/history" title="Open the class record and add proof">
              <Upload aria-hidden /> Upload class proof
            </Link>
          </Button>
        ) : null}
        <Button variant="outline" asChild>
          <Link to="/requests">
            <FileStack aria-hidden /> My requests
          </Link>
        </Button>
      </nav>

      <Dialog open={target !== null} onOpenChange={(open) => (open ? null : setTarget(null))}>
        {target && data ? (
          <DialogContent title={target.kind === 'in' ? (target.session.sessionType === 'DAILY' ? 'Time in' : 'Clock in') : target.session.sessionType === 'DAILY' ? 'Time out' : 'Clock out'} size="lg">
            <ClockDialog session={target.session} kind={target.kind} timeZone={data.timeZone} offlineOnly={target.offline} onDone={done} onClose={() => setTarget(null)} />
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
